import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { PDFDownloadLink, usePDF } from '@react-pdf/renderer';
import { Document, Page, pdfjs } from 'react-pdf';
import { throttle } from 'lodash';
import { useTranslation } from 'react-i18next';
import ProductionRequestPdf from './ProductionRequestPdf';
import { IconButton } from '@material-ui/core';
import { color } from 'styles/utils';
import SaveIcon from 'components/base/icons/SaveIcon';
import PrintIcon from 'components/base/icons/PrintIcon';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { useProjectDetailStore } from 'store/local/projectDetail';
import { Skeleton } from '@material-ui/lab';
import { useReactToPrint } from 'react-to-print';
import { useProject } from 'lib/hooks/project/useProject';
import ProductionRequestPdfDocument from './ProductionRequestPdfDocument';
import { Implant, IndicationPart, Material } from 'lib/types/projectTypes';
import ProductionRequestPdfDoc from './ProductionRequestPdfDoc';

export interface ProductionRequestViewerProps {}

function ProductionRequestViewer({}: ProductionRequestViewerProps) {
  const { bridge, teeth } = useProjectDetailStore();
  const { user, projectInfo, language, indicationInfo } = useProject();
  const contentRef = useRef<HTMLDivElement>(null);

  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const { t } = useTranslation();

  const { partList = [], materialList = [] } = indicationInfo as {
    partList: IndicationPart[];

    materialList: Material[];
    implantList: Implant[];
  };

  const invoiceScrollRef = useRef<HTMLDivElement | null>(null);

  const productionRequestPdf = useMemo(
    () => (
      <ProductionRequestPdf
        language={language}
        projectInfo={projectInfo}
        user={user}
        teeth={teeth}
        bridge={bridge}
        partList={partList}
        materialList={materialList}
      />
    ),
    [projectInfo, user, teeth, bridge],
  );

  const [instance, updateInstance] = usePDF({
    document: <ProductionRequestPdfDocument pdf={productionRequestPdf} />,
  });

  // const [instance, updateInstance] = usePDF({
  //   document: (
  //     <ProductionRequestPdfDoc
  //       language={language}
  //       projectInfo={projectInfo}
  //       user={user}
  //       teeth={teeth}
  //       bridge={bridge}
  //       partList={partList}
  //       materialList={materialList}
  //     />
  //   ),
  // });

  const reactToPrintFn = useReactToPrint({
    bodyClass: 'react-pdf__Document',
    contentRef,
  });

  // SECTION: function
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handlePrevPage = () => {
    if (invoiceScrollRef.current) {
      let targetPdfHeight =
        invoiceScrollRef.current?.querySelector('.react-pdf__Document')?.clientHeight || 0;
      targetPdfHeight = targetPdfHeight + 2;
      const pageScrollTop = targetPdfHeight * (pageNumber - 2);
      invoiceScrollRef.current.scrollTop = pageScrollTop;
    }

    setPageNumber(pageNumber - 1);
  };

  const handleNextPage = () => {
    if (invoiceScrollRef.current) {
      // let targetPdfHeight =
      //   invoiceScrollRef.current?.querySelector('.react-pdf__Document')?.clientHeight || 0;
      // targetPdfHeight = targetPdfHeight + 2;
      const pageScrollTop = 634 * pageNumber;
      invoiceScrollRef.current.scrollTop = pageScrollTop;
    }

    setPageNumber(pageNumber + 1);
  };

  const handlePdfScroll = throttle((e: any) => {
    const currentScrollTop = invoiceScrollRef.current?.scrollTop || 0;
    let targetPdfHeight =
      invoiceScrollRef.current?.querySelector('.react-pdf__Document')?.clientHeight || 0;
    targetPdfHeight = targetPdfHeight + 2;

    const currentPageNumber = Math.floor(currentScrollTop / targetPdfHeight) + 1;
    setPageNumber(currentPageNumber);
  }, 300);

  // SECTION: DidMount
  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  }, []);

  // SECTION: DidUpdate

  return (
    <StyledProductionRequestViewer data-component-name="ProductionRequestViewer">
      <div className="productionRequestViewer__wrapper">
        <div
          className="productionRequestViewer__pdf_container"
          ref={invoiceScrollRef}
          onScroll={handlePdfScroll}
        >
          <div className="productionRequestViewer__pdf_box" onScroll={handlePdfScroll}>
            <div className="productionRequestViewer__pdf" onScroll={handlePdfScroll}>
              {instance.url ? (
                Array.from({ length: numPages }).map((item, idx) => (
                  <Document
                    key={idx}
                    // file={require('./sample.pdf')}
                    file={instance.url}
                    loading={() => {
                      return <PdfSkeleton />;
                    }}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={error => console.log(error.message)}
                  >
                    <Page width={433} pageNumber={idx + 1} />
                  </Document>
                ))
              ) : (
                <PdfSkeleton />
              )}
            </div>
          </div>
        </div>

        <div className="productionRequestViewer__button_container">
          <PDFDownloadLink
            fileName="pdf_name"
            document={
              <ProductionRequestPdfDoc
                language={language}
                projectInfo={projectInfo}
                user={user}
                teeth={teeth}
                bridge={bridge}
                partList={partList}
                materialList={materialList}
              />
            }
            className="productionRequestViewer__button save"
          >
            <PdfButton
              disabled={!instance.url}
              icon={<SaveIcon />}
              onClick={() => {}}
              // name={t('PDF')}
              name={'PDF'}
            />
          </PDFDownloadLink>
          <div className="middleLine"></div>
          <PdfButton
            disabled={!instance.url}
            icon={<PrintIcon />}
            onClick={() => {
              reactToPrintFn();
            }}
            name={t('GLOBAL_PRINT')}
            className="productionRequestViewer__button print"
          />
        </div>

        <div className="productionRequestViewer__page_box">
          <span className="curr_page">{pageNumber}</span>
          <span className="page_bar">{' / '}</span>
          <span className="total_page">{numPages}</span>
        </div>
        <div className="productionRequestViewer__pagination_box">
          <IconButton
            className="pagination_button prev"
            onClick={handlePrevPage}
            disabled={pageNumber <= 1}
          >
            <NavigateBeforeIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            className="pagination_button next"
            onClick={handleNextPage}
            disabled={pageNumber >= numPages}
          >
            <NavigateNextIcon fontSize="inherit" />
          </IconButton>
        </div>
      </div>
      <div className="productionRequestViewer__pdf_hide" ref={contentRef}>
        {instance.url &&
          Array.from({ length: numPages }).map((item, idx) => (
            <Document
              key={idx}
              file={instance.url}
              loading={() => {
                return <PdfSkeleton />;
              }}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={error => console.log(error.message)}
            >
              <Page width={775} pageNumber={idx + 1} />
            </Document>
          ))}
      </div>
    </StyledProductionRequestViewer>
  );
}

const StyledProductionRequestViewer = styled.div`
  padding: 0 10px;
  position: relative;
  .productionRequestViewer__wrapper {
    position: relative;
    .productionRequestViewer__page_box {
      position: absolute;
      top: 20px;
      left: 20px;
      span {
        font-size: 18px;
        font-weight: 400;
      }
      .curr_page {
        color: #222;
      }
      .page_bar,
      .total_page {
        color: #888;
      }
    }
    .productionRequestViewer__pdf_container {
      position: relative;
      height: 654px;
      background-color: #ced2df;
      overflow-y: auto;
      &::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera*/
      }
      .productionRequestViewer__pdf_box {
        height: 654px;
        padding-top: 20px;
        .productionRequestViewer__pdf {
          width: 433px;
          margin: 0 auto;
          .react-pdf__Document {
            width: 433px;
            &:not(:last-child) {
              height: 614px;
              margin-bottom: 20px;
            }
            &:last-child {
              height: 634px;
              padding-bottom: 20px;
            }
          }
        }
      }
    }
    .productionRequestViewer__pagination_box {
      position: absolute;
      display: flex;
      column-gap: 30px;
      bottom: 134px;
      left: 50%;
      transform: translate(-50%, -0);
      .pagination_button {
        font-size: 30px;
        padding: 0;
        width: 28px;
        height: 28px;
        border: 1px solid #e1e1e7;
        border-radius: 50%;
      }
    }

    .productionRequestViewer__button_container {
      padding: 30px 10px 10px 10px;
      display: flex;
      height: 100%;
      .save {
        width: 100%;
      }
      .middleLine {
        width: 1px;
        height: auto;
        background-color: #e1e1e7;
      }
    }
  }
  .productionRequestViewer__pdf_hide {
    position: absolute;
    visibility: hidden;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .text_test {
    width: 200px;
  }
`;

export default React.memo(ProductionRequestViewer);

interface PdfButtonProps {
  icon?: any;
  hoverIcon?: any;
  disabled?: boolean;
  onClick: () => void;
  name?: string;
  className?: string;
}

function PdfButton({
  icon,
  hoverIcon,
  disabled = false,
  onClick,
  name,
  className,
}: PdfButtonProps) {
  const [hover, setHover] = useState<boolean>(false);

  return (
    <StyledPdfButton data-component-name="PdfButton" $hover={hover} $disabled={disabled}>
      <IconButton
        className={`pdfButton__wrapper ${className}`}
        onClick={onClick}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="pdfButton__box">
          <div className="pdfButton__icon">
            {!!icon &&
              typeof icon !== 'string' &&
              React.cloneElement(icon, {
                color: !!disabled ? color.gray_b5 : !!hover ? '#00a6e2' : '#444',
              })}
            {!!icon && typeof icon === 'string' && (
              <>
                {!hover && <img src={icon} />}
                {!!hoverIcon && !!hover && <img src={hoverIcon} />}
              </>
            )}
          </div>
          {!!name && <span className="pdfButton__name">{name}</span>}
        </div>
      </IconButton>
    </StyledPdfButton>
  );
}

const StyledPdfButton = styled.div<{ $hover: boolean; $disabled: boolean }>`
  width: 100%;
  .pdfButton__wrapper {
    width: 100%;
    height: 70px;
    border-radius: 0;
    &:hover {
      cursor: pointer;
      background-color: white;
    }
  }
  .pdfButton__box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 10px;
    .pdfButton__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .pdfButton__name {
      font-size: 18px;
      font-weight: 400;
      color: ${({ $hover, $disabled }) =>
        !!$disabled ? color.gray_b5 : !!$hover ? '#00a6e2' : '#444'};
    }
  }
`;

interface PdfSkeletonProps {}

function PdfSkeleton({}: PdfSkeletonProps) {
  return (
    <StylePdfSkeleton>
      <StyledSkeleton animation="wave" variant="rect" height={614} width={433} />
    </StylePdfSkeleton>
  );
}

const StylePdfSkeleton = styled.div<{}>``;

const StyledSkeleton = styled(Skeleton)`
  border-radius: 10px;
  background-color: #f4f5fa;
`;
